/**
 * 统一请求封装
 * - 基于 fetch 封装，支持 GET / POST / PUT / DELETE
 * - 统一超时控制、错误处理、JSON 解析
 * - 通过 REACT_APP_API_BASE_URL 环境变量配置后端地址
 */

import { API_BASE_URL } from './env';

// 最终 baseUrl：环境变量强制覆盖 > 环境配置 > 本地代理 /api
const BASE_URL = API_BASE_URL || '/api';
const TIMEOUT = 10000; // 10 秒超时

/**
 * 核心请求函数
 * @param {string} url     接口路径（相对 BASE_URL）
 * @param {object} options fetch 配置项
 * @returns {Promise<any>} 解析后的响应数据
 */
async function request(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT);

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    signal: controller.signal,
    ...options,
  };

  try {
    const response = await fetch(`${BASE_URL}${url}`, config);
    clearTimeout(timer);

    if (!response.ok) {
      throw new Error(`请求失败：HTTP ${response.status}`);
    }

    const result = await response.json();

    // 约定后端返回格式 { code: 0, data: ..., message: '' }
    if (result.code !== undefined && result.code !== 0) {
      throw new Error(result.message || '业务处理失败');
    }

    return result.data !== undefined ? result.data : result;
  } catch (error) {
    clearTimeout(timer);
    if (error.name === 'AbortError') {
      throw new Error('请求超时，请稍后重试');
    }
    throw error;
  }
}

export const http = {
  get: (url, params) => {
    const query = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    return request(`${url}${query}`, { method: 'GET' });
  },
  post: (url, data) =>
    request(url, { method: 'POST', body: JSON.stringify(data) }),
  put: (url, data) =>
    request(url, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (url) => request(url, { method: 'DELETE' }),
};

export default request;
